import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { FormDrawer } from 'Components/ui';
import type { PaginationLabels } from 'Components/ui';
import { PRIORITY_ID } from 'Constants/priority';
import useTranslation from 'Hooks/useTranslation';
import useEligibleEmployeesForApproval from '../../Hooks/useEligibleEmployeesForApproval';
import EvidenceSection from './EvidenceSection';
import EmployeeSelectionPanel from './EmployeeSelectionPanel';
import useBulkApproveForm from './useBulkApproveForm';
import type { DevelopmentPlanEntryI } from 'types';

interface ApproveParams {
  userCourseIds: string[];
  file: File | null;
  evaluationDate?: Date;
  courseId: string;
  instructorId: string | null;
  instructorUserId: string | null;
}

interface Props {
  open: boolean;
  course: DevelopmentPlanEntryI | null;
  onClose: () => void;
  onApprove: (params: ApproveParams) => Promise<boolean>;
}

const BulkApproveDrawer = ({ open, course, onClose, onApprove }: Props) => {
  const { t2 } = useTranslation('EmployeeAssess');

  const courseId = course?.Course?.id ?? null;
  const priorityId = course?.Course?.Priority?.id ?? '';
  const currentUserCourseId = course?.userCourseId ?? null;
  const isNormativa = priorityId === PRIORITY_ID.normativa;
  const isTecnica = priorityId === PRIORITY_ID.tecnica;

  const { form, expanded, setExpanded, page, setPage, pageSize, setPageSize, search, setSearch, debouncedSearch } =
    useBulkApproveForm({ open, priorityId, currentUserCourseId });

  const { rows, totalEmployees, loading } = useEligibleEmployeesForApproval({
    courseId,
    enabled: open,
    page,
    pageSize,
    fullName: debouncedSearch || undefined,
  });

  const selectedIds = useWatch({ control: form.control, name: 'selectedUserCourseIds' }) ?? [];
  const watchedFile = useWatch({ control: form.control, name: 'file' });
  const selectedCount = selectedIds.length;
  const otherEligibleCount = Math.max(0, totalEmployees - 1);

  const visibleRowIds = useMemo(() => new Set(rows.map(r => r.id)), [rows]);

  const selectedSet: Set<string> = useMemo(() => new Set(selectedIds), [selectedIds]);

  const handleToggle = (id: string) => {
    if (id === currentUserCourseId) return;
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    if (currentUserCourseId) next.add(currentUserCourseId);
    form.setValue('selectedUserCourseIds', Array.from(next), { shouldValidate: false });
  };

  const handleToggleAll = () => {
    const allVisibleSelected = Array.from(visibleRowIds).every(id => selectedIds.includes(id));
    // Lo seleccionado en otras páginas se conserva; el cambio es sobre las visibles.
    const next = new Set(selectedIds.filter(id => !visibleRowIds.has(id)));
    if (!allVisibleSelected) {
      visibleRowIds.forEach(id => {
        if (id !== currentUserCourseId) next.add(id);
      });
    }
    if (currentUserCourseId) next.add(currentUserCourseId);
    form.setValue('selectedUserCourseIds', Array.from(next), { shouldValidate: false });
  };

  const handleSubmit = form.handleSubmit(async values => {
    if (!courseId) return;
    const ok = await onApprove({
      userCourseIds: values.selectedUserCourseIds,
      file: values.file,
      evaluationDate: values.evaluationDateTouched ? values.evaluationDate : undefined,
      courseId,
      // Manda solo la fuente elegida; el backend nunca debe recibir ambas.
      instructorId: values.instructorType === 'external' ? (values.instructorId ?? null) : null,
      instructorUserId: values.instructorType === 'internal' ? (values.instructorUserId ?? null) : null,
    });
    if (ok) onClose();
  });

  if (!course) return null;

  const isSubmitting = form.formState.isSubmitting;

  const paginationLabels: PaginationLabels = {
    showing: t2('pagination_showing') as string,
    of: t2('pagination_of') as string,
    page: t2('pagination_page') as string,
    prev: t2('pagination_prev') as string,
    next: t2('pagination_next') as string,
    perPage: t2('pagination_per_page') as string,
  };

  return (
    <FormDrawer
      open={open}
      formId="bulk-approve-form"
      width="lg"
      title={t2('AssessCourse') as string}
      subtitle={course.Course.name}
      labels={{
        close: t2('Close') as string,
        cancel: t2('Close') as string,
        // Normativa se aprueba sola: sin etiqueta de envío, el cajón no ofrece el botón.
        submit: isNormativa
          ? undefined
          : selectedCount > 1
            ? (t2('approve_button_count') as string).replace('{n}', String(selectedCount))
            : (t2('Approve') as string),
      }}
      submitting={isSubmitting}
      canSubmit={!isTecnica || !!watchedFile}
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <EvidenceSection
        control={form.control}
        setValue={form.setValue}
        priorityId={priorityId}
        expanded={expanded}
        toggleExpanded={() => setExpanded(prev => !prev)}
        eligibleCount={otherEligibleCount}
      />
      {expanded ? (
        <EmployeeSelectionPanel
          rows={rows}
          totalEmployees={totalEmployees}
          loading={loading}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          search={search}
          onSearchChange={setSearch}
          selected={selectedSet}
          onToggle={handleToggle}
          onToggleAll={handleToggleAll}
          selectedCount={selectedCount}
          paginationLabels={paginationLabels}
        />
      ) : null}
    </FormDrawer>
  );
};

export default BulkApproveDrawer;
